// tu-clearance.js -- clearance video, play-on-scroll (2026-09-27).
//
// One native <video>, no rail, no YouTube -- deliberately not routed through
// assets/tu-video.js (the Target Reviews focus carousel) or tu-dep-media.js
// (the department slider): neither owns a single always-on video with no
// timer, no sound control and no other cards to manage. This is the whole
// job: play it once >=25% is on screen, pause the moment it leaves (preload
// is "none" -- nothing downloads before that), and never fight Low Power
// Mode or prefers-reduced-motion.
//
// The site just shipped a fix for exactly that failure mode elsewhere
// (Docs/status/open-topics.md, 2026-09-27, "review videos unplayable when
// autoplay is refused" -- tu-video.js's .is-manual state). Same diagnosis
// applies here in miniature: an autoplay attempt can be refused outright
// (Low Power Mode, or any browser that just won't), and a card that fakes
// "playing" anyway strands the visitor behind a poster with nothing to
// press. This section is a single native <video>, not an iframe, so the fix
// is simpler than tu-video.js's YouTube case: a tap in this document IS a
// user gesture, so `controls` (once shown) already has a working native
// play button, and a click handler on the element is a legitimate second
// route to the same play() call.
(function () {
  var reducedQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = !!(reducedQuery && reducedQuery.matches);
  // ?tu-lpm=1 previews the Low Power Mode experience in any browser (same
  // switch as tu-video.js): no autoplay attempt, straight to the refused
  // state, and the loop runs as a muted <video> because only Safari animates
  // an MP4 given to an <img>.
  var simLPM = /[?&]tu-lpm=1(&|$)/.test(location.search);

  function bind(section) {
    if (section.tuClrBound) return;
    section.tuClrBound = true;

    var video = section.querySelector('[data-tu-clr-video]');
    if (!video) return;

    // Refused, or reduced motion: fall back to the browser's own controls so
    // there is always a visible, working way to start the clip. Only ever
    // ADDS the attribute -- never removed once shown, so a card that has
    // gone manual does not silently lose its play button again later.
    function goManual() {
      if (!video.hasAttribute('controls')) video.setAttribute('controls', '');
      showLoop();
    }

    // 2026-09-29: Low Power Mode refuses the autoplay outright (measured on a
    // real iPhone, iOS 26). Safari still animates images, and it plays a
    // silent MP4 given to an <img>, so on a refusal the same clip is laid over
    // the player as an image (tu-system.css .tu-clr__loop); the native player
    // is hidden while it runs. Browsers that cannot do this fire `error` and
    // keep the controls shown above. Never under reduced motion.
    var loopTried = false;
    function showLoop() {
      if (reduced || loopTried) return;
      loopTried = true;
      var srcEl = video.querySelector('source');
      var src = srcEl ? srcEl.getAttribute('src') : video.getAttribute('src');
      var frame = video.parentNode;
      if (!src || !frame) return;
      var img = document.createElement(simLPM ? 'video' : 'img');
      img.className = 'tu-clr__loop'; img.setAttribute('aria-hidden', 'true');
      if (simLPM) { img.muted = true; img.loop = true; img.playsInline = true; img.setAttribute('muted', ''); img.setAttribute('playsinline', ''); }
      else img.alt = '';
      img.addEventListener(simLPM ? 'loadeddata' : 'load', function () { frame.classList.add('has-loop'); });
      img.addEventListener('error', function () { img.remove(); frame.classList.remove('has-loop'); });
      img.src = src;
      frame.appendChild(img);
      if (simLPM) { var pp = img.play(); if (pp && pp.catch) pp.catch(function () {}); }
    }

    if (reduced) {
      // Spec: never autoplay under reduced motion, controls present from
      // the start, a tap still plays it.
      goManual();
    }

    function attempt() {
      if (reduced) return;
      if (simLPM) { goManual(); return; }
      var p = video.play();
      if (p && p.catch) {
        // The common, synchronous case (measured with the site's own Low
        // Power Mode audit model, tu-video.js precedent): play() rejects
        // outright and the catch fires almost immediately.
        p.catch(function () { goManual(); });
      }
      // Belt and braces for the LPM behaviour that does NOT reject the
      // promise, it just never actually starts (same note in tu-video.js's
      // cardPlay, 2026-09-27): .paused is trustworthy a short beat later,
      // once a real accept would already have flipped it to false.
      clearTimeout(video._tuClrCheck);
      video._tuClrCheck = setTimeout(function () {
        if (video.paused) goManual();
      }, 1200);
    }

    function stop() {
      video.pause();
    }

    // No tap handler of our own (audit fix, 2026-09-27). A `pointerdown` ->
    // play() listener used to sit here; measured on the mirror it broke the
    // native play button in Chromium (desktop and phone): our play() ran
    // first, then the button's own toggle saw a playing video and paused it
    // again, so the press did nothing. Whenever autoplay is refused or
    // reduced motion is on, goManual() above turns on `controls`, and the
    // browser's own play button and click-to-play are the working route.

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { attempt(); } else { stop(); }
        });
      }, { threshold: 0.25 });
      io.observe(video);
    } else {
      // No IntersectionObserver: still respect reduced motion, otherwise
      // just try once. No off-screen gate to lose -- this browser gets the
      // old unconditional-autoplay behaviour, not a broken one.
      attempt();
    }
  }

  function init() {
    document.querySelectorAll('[data-tu-clr]').forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();
